import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, Search, AlertCircle, GitBranch, Zap } from 'lucide-react';
import { TsGraphVisualizer } from './TsGraphVisualizer';
import { TsNode, TsGraphData, TsEdge, getCategoryColor } from './types';

type Stack = 'frontend' | 'backend' | 'both';

const STACK_OPTIONS: Array<{ id: Stack; label: string }> = [
  { id: 'frontend', label: 'Frontend' },
  { id: 'backend', label: 'Backend' },
  { id: 'both', label: 'Both' },
];

interface FrontendTypeScriptGraphPageProps {
  onBack: () => void;
}

/**
 * Namespace one stack's graph so frontend and backend ids can't collide when
 * merged: node/edge/metadata ids get a `fe:`/`be:` prefix and every node is
 * tagged with its stack.
 */
function namespaceGraph(data: TsGraphData, prefix: string, stack: 'frontend' | 'backend'): TsGraphData {
  return {
    nodes: data.nodes.map(n => ({
      ...n,
      id: prefix + n.id,
      file: n.file ? prefix + n.file : undefined,
      stack,
    })),
    edges: data.edges.map(e => ({ ...e, source: prefix + e.source, target: prefix + e.target })),
    metadata: {
      ...data.metadata,
      unusedExports: data.metadata.unusedExports.map(u => ({ ...u, file: prefix + u.file })),
      circularDependencies: data.metadata.circularDependencies.map(c => ({
        ...c,
        nodes: c.nodes.map(n => prefix + n),
      })),
      apiCalls: data.metadata.apiCalls?.map(c => ({ ...c, file: prefix + c.file })),
      apiRoutes: data.metadata.apiRoutes?.map(r => ({ ...r, file: prefix + r.file })),
    },
  };
}

/**
 * Cross-stack `calls` edges: match frontend endpoint calls against backend
 * route registrations. Exact normalized-path match first; then segment-prefix
 * fallback for baseUrl-style callers (`${API_BASE}/history` + "/sessions" on
 * the call site — the frontend path is a whole-segment prefix of the route).
 */
function buildCallsEdges(fe: TsGraphData, be: TsGraphData): TsEdge[] {
  const calls = fe.metadata.apiCalls || [];
  const routes = be.metadata.apiRoutes || [];
  if (calls.length === 0 || routes.length === 0) return [];

  const exact = new Map<string, Set<string>>();
  for (const r of routes) {
    if (!exact.has(r.path)) exact.set(r.path, new Set());
    exact.get(r.path)!.add(r.file);
  }

  const edges: TsEdge[] = [];
  const seen = new Set<string>();
  for (const call of calls) {
    let targets = exact.get(call.path);
    if (!targets) {
      // Prefix fallback: /history matches /history/sessions etc.
      targets = new Set(
        routes.filter(r => r.path.startsWith(call.path + '/')).map(r => r.file)
      );
    }
    for (const file of Array.from(targets)) {
      const key = `${call.file}→${file}`;
      if (!seen.has(key)) {
        seen.add(key);
        edges.push({ source: call.file, target: file, type: 'calls', endpoint: call.path });
      }
    }
  }
  return edges;
}

export const FrontendTypeScriptGraphPage: React.FC<FrontendTypeScriptGraphPageProps> = ({ onBack }) => {
  const [tsData, setTsData] = useState<TsGraphData | null>(null);
  const [rustData, setRustData] = useState<TsGraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stack, setStack] = useState<Stack>('both');
  const [selectedNode, setSelectedNode] = useState<TsNode | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [showCyclesOnly, setShowCyclesOnly] = useState(false);

  // Load both graphs; tolerate either one missing so a partial deploy still shows the other.
  useEffect(() => {
    const load = async (url: string): Promise<TsGraphData | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return (await res.json()) as TsGraphData;
      } catch {
        return null;
      }
    };
    (async () => {
      const [ts, rust] = await Promise.all([
        load('/ts-dependency-graph.json'),
        load('/rust-dependency-graph.json'),
      ]);
      setTsData(ts ? namespaceGraph(ts, 'fe:', 'frontend') : null);
      setRustData(rust ? namespaceGraph(rust, 'be:', 'backend') : null);
      if (!ts && !rust) setError('Failed to load dependency graph data');
      setLoading(false);
    })();
  }, []);

  // Merge + filter down to the active stack. In the Both view, cross-stack
  // `calls` edges connect frontend callers to backend route handlers.
  const graphData: TsGraphData | null = useMemo(() => {
    const parts: TsGraphData[] = [];
    if ((stack === 'frontend' || stack === 'both') && tsData) parts.push(tsData);
    if ((stack === 'backend' || stack === 'both') && rustData) parts.push(rustData);
    if (parts.length === 0) return null;
    const callsEdges = stack === 'both' && tsData && rustData ? buildCallsEdges(tsData, rustData) : [];
    return {
      nodes: parts.flatMap(p => p.nodes),
      edges: [...parts.flatMap(p => p.edges), ...callsEdges],
      metadata: {
        filesScanned: parts.reduce((s, p) => s + p.metadata.filesScanned, 0),
        nodesCount: parts.reduce((s, p) => s + p.nodes.length, 0),
        edgesCount: parts.reduce((s, p) => s + p.edges.length, 0),
        unusedExports: parts.flatMap(p => p.metadata.unusedExports),
        unusedExportsCount: parts.reduce((s, p) => s + p.metadata.unusedExportsCount, 0),
        circularDependencies: parts.flatMap(p => p.metadata.circularDependencies),
        circularDependenciesCount: parts.reduce((s, p) => s + p.metadata.circularDependenciesCount, 0),
      },
    };
  }, [tsData, rustData, stack]);

  // Category chips grouped by stack (frontend / backend), each with counts.
  const categoryGroups = useMemo(() => {
    if (!graphData) return [];
    const groups: Array<{
      stack: 'frontend' | 'backend';
      cats: Array<{ id: string; count: number; color: string }>;
    }> = [];
    for (const s of ['frontend', 'backend'] as const) {
      const counts = new Map<string, number>();
      for (const n of graphData.nodes) {
        if (n.stack === s) counts.set(n.category, (counts.get(n.category) || 0) + 1);
      }
      if (counts.size > 0) {
        groups.push({
          stack: s,
          cats: Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([id, count]) => ({ id, count, color: getCategoryColor(id) })),
        });
      }
    }
    return groups;
  }, [graphData]);

  // Circular node ids for the active stack.
  const circularNodeIds = useMemo(() => {
    const ids = new Set<string>();
    if (!graphData) return ids;
    for (const cycle of graphData.metadata.circularDependencies) {
      for (const n of cycle.nodes) ids.add(n);
    }
    return ids;
  }, [graphData]);

  // Dependents / dependencies for the selected node.
  const nodeDetails = useMemo(() => {
    if (!graphData || !selectedNode) return null;
    const dependents = new Set<string>();
    const dependencies = new Set<string>();
    for (const edge of graphData.edges) {
      if (edge.target === selectedNode.id) dependents.add(edge.source);
      if (edge.source === selectedNode.id) dependencies.add(edge.target);
    }
    return {
      dependents: Array.from(dependents),
      dependencies: Array.from(dependencies),
      isCircular: circularNodeIds.has(selectedNode.id),
    };
  }, [graphData, selectedNode, circularNodeIds]);

  // ── Impact analysis ────────────────────────────────────────────────────
  // "What breaks if I change this?" — reverse BFS over ALL edge types
  // (imports, uses, and cross-stack calls), so a backend function's ripple
  // continues through its HTTP endpoint into the frontend components.
  const [impactMap, setImpactMap] = useState<Map<string, number> | null>(null);

  const runImpact = useCallback(() => {
    if (!graphData || !selectedNode) return;
    const rev = new Map<string, string[]>();
    for (const e of graphData.edges) {
      if (!rev.has(e.target)) rev.set(e.target, []);
      rev.get(e.target)!.push(e.source);
    }
    const seen = new Map<string, number>([[selectedNode.id, 0]]);
    let frontier = [selectedNode.id];
    for (let d = 1; d <= 12 && frontier.length > 0; d++) {
      const next: string[] = [];
      for (const id of frontier) {
        for (const dep of rev.get(id) || []) {
          if (!seen.has(dep)) {
            seen.set(dep, d);
            next.push(dep);
          }
        }
      }
      frontier = next;
    }
    setImpactMap(seen);
  }, [graphData, selectedNode]);

  // Impact is scoped to one node — clear it when the selection changes.
  useEffect(() => {
    setImpactMap(null);
  }, [selectedNode?.id]);

  // Impacted file-level nodes grouped by depth (symbols excluded for readability).
  const impactSummary = useMemo(() => {
    if (!impactMap || !graphData) return null;
    const nodeById = new Map(graphData.nodes.map(n => [n.id, n]));
    const byDepth = new Map<number, TsNode[]>();
    let uiComponents = 0;
    for (const [id, depth] of Array.from(impactMap.entries())) {
      if (depth === 0) continue;
      const node = nodeById.get(id);
      if (!node || node.category === 'export') continue;
      if (!byDepth.has(depth)) byDepth.set(depth, []);
      byDepth.get(depth)!.push(node);
      if (node.id.endsWith('.tsx')) uiComponents++;
    }
    const depths = Array.from(byDepth.keys()).sort((a, b) => a - b);
    for (const d of depths) byDepth.get(d)!.sort((a, b) => a.id.localeCompare(b.id));
    const total = depths.reduce((s, d) => s + byDepth.get(d)!.length, 0);
    return { byDepth, depths, total, uiComponents };
  }, [impactMap, graphData]);

  const handleStackChange = (next: Stack) => {
    setStack(next);
    // Categories differ per stack — stale filters would silently blank the graph.
    setFilterCategories([]);
    setSelectedNode(null);
  };

  const handleCategoryToggle = (category: string) => {
    setFilterCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dependency graph...</p>
        </div>
      </div>
    );
  }

  if (error || !graphData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-700 font-medium mb-2">Error loading graph</p>
          <p className="text-gray-600 text-sm mb-4">{error || 'No graph data for this stack'}</p>
          <button onClick={onBack} className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600">
            Back to Chat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header — two compact rows to maximise vertical space for the graph */}
      <div className="border-b border-gray-200 bg-white px-3 py-2">
        {/* Row 1: title · stack toggle · search · view toggles · stats */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onBack}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
            title="Back to chat"
          >
            <ChevronLeft size={18} />
          </button>
          <GitBranch size={15} className="text-primary-500" />
          <h1 className="text-sm font-semibold whitespace-nowrap">Dependency Graph</h1>

          {/* Stack toggle */}
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs ml-1">
            {STACK_OPTIONS.map((opt, idx) => (
              <button
                key={opt.id}
                onClick={() => handleStackChange(opt.id)}
                disabled={
                  (opt.id === 'frontend' && !tsData) ||
                  (opt.id === 'backend' && !rustData)
                }
                className={`px-2.5 py-1 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  idx > 0 ? 'border-l border-gray-300' : ''
                } ${stack === opt.id ? 'bg-primary-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Compact search (~15 chars) */}
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-40 pl-6 pr-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* View toggles */}
          <button
            onClick={() => setShowUnusedOnly(!showUnusedOnly)}
            className={`px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
              showUnusedOnly
                ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Unused ({graphData.metadata.unusedExportsCount})
          </button>
          <button
            onClick={() => setShowCyclesOnly(!showCyclesOnly)}
            className={`px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
              showCyclesOnly
                ? 'bg-red-100 text-red-700 ring-1 ring-red-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Cycles ({graphData.metadata.circularDependenciesCount})
          </button>

          <div className="text-[11px] text-gray-500 ml-auto whitespace-nowrap">
            {graphData.metadata.filesScanned} files • {graphData.nodes.length} nodes • {graphData.edges.length} edges
          </div>
        </div>

        {/* Row 2: category chips grouped by stack */}
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-1.5">
          {categoryGroups.map(group => (
            <div key={group.stack} className="flex items-center gap-1 flex-wrap">
              {categoryGroups.length > 1 && (
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    group.stack === 'frontend'
                      ? 'bg-blue-50 text-blue-600'
                      : 'bg-orange-50 text-orange-600'
                  }`}
                >
                  {group.stack === 'frontend' ? 'FE' : 'BE'}
                </span>
              )}
              {group.cats.map(cat => (
                <button
                  key={`${group.stack}-${cat.id}`}
                  onClick={() => handleCategoryToggle(cat.id)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-all ${
                    filterCategories.includes(cat.id)
                      ? 'ring-1'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={
                    filterCategories.includes(cat.id)
                      ? { backgroundColor: cat.color + '20', color: cat.color }
                      : undefined
                  }
                >
                  {cat.id} ({cat.count})
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-2 p-2 overflow-hidden">
        {/* Graph */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <TsGraphVisualizer
            graphData={graphData}
            onNodeSelect={setSelectedNode}
            selectedNodeId={selectedNode?.id}
            filterCategories={filterCategories}
            searchText={searchText}
            showUnusedOnly={showUnusedOnly}
            showCyclesOnly={showCyclesOnly}
            circularNodeIds={circularNodeIds}
            impactMap={impactMap}
          />
        </div>

        {/* Detail panel */}
        {selectedNode && nodeDetails && (
          <div className="w-80 bg-white rounded-lg shadow-sm border border-gray-200 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div>
                <h2 className="font-semibold text-gray-900 truncate">{selectedNode.name}</h2>
                <p className="text-xs text-gray-500 truncate">{selectedNode.id}</p>
                <div className="mt-2 flex gap-1.5">
                  <span className="inline-block px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">
                    {selectedNode.category}
                  </span>
                  {selectedNode.stack && (
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        selectedNode.stack === 'backend'
                          ? 'bg-orange-50 text-orange-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      {selectedNode.stack}
                    </span>
                  )}
                </div>
              </div>

              {nodeDetails.isCircular && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700 font-medium">⚠ Part of circular dependency</p>
                </div>
              )}

              {/* Impact analysis — full transitive ripple, incl. cross-stack */}
              <button
                onClick={() => (impactMap ? setImpactMap(null) : runImpact())}
                className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  impactMap
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100 ring-1 ring-amber-200'
                }`}
              >
                <Zap size={13} />
                {impactMap ? 'Clear Impact' : 'Impact — what breaks if I change this?'}
              </button>

              {impactSummary && (
                <div className="space-y-2">
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800">
                      <strong>{impactSummary.total} file{impactSummary.total === 1 ? '' : 's'} affected</strong>
                      {impactSummary.uiComponents > 0 && (
                        <> · <strong>{impactSummary.uiComponents} UI component{impactSummary.uiComponents === 1 ? '' : 's'}</strong></>
                      )}
                      {impactSummary.total === 0 && ' — nothing depends on this node'}
                    </p>
                  </div>
                  {impactSummary.depths.map(depth => {
                    const nodes = impactSummary.byDepth.get(depth)!;
                    return (
                      <div key={depth}>
                        <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Depth {depth} ({nodes.length})
                        </h4>
                        <div className="space-y-0.5 max-h-36 overflow-y-auto">
                          {nodes.map(n => (
                            <div key={n.id} className="flex items-center gap-1 text-xs truncate">
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: getCategoryColor(n.category) }}
                              />
                              <span className={n.id.endsWith('.tsx') ? 'text-pink-700 font-medium' : 'text-gray-700'}>
                                {n.id}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Dependents ({nodeDetails.dependents.length})</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {nodeDetails.dependents.length === 0 ? (
                    <p className="text-xs text-gray-500">None</p>
                  ) : (
                    nodeDetails.dependents.map(dep => (
                      <div key={dep} className="text-xs text-gray-700 truncate hover:text-primary-600 cursor-pointer">
                        {dep}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Dependencies ({nodeDetails.dependencies.length})</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {nodeDetails.dependencies.length === 0 ? (
                    <p className="text-xs text-gray-500">None</p>
                  ) : (
                    nodeDetails.dependencies.map(dep => (
                      <div key={dep} className="text-xs text-gray-700 truncate hover:text-primary-600 cursor-pointer">
                        {dep}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {selectedNode.exports && selectedNode.exports.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Exports ({selectedNode.exports.length})</h3>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedNode.exports.map(exp => {
                      const isUnused = graphData.metadata.unusedExports.some(
                        u => u.file === selectedNode.id && u.export === exp
                      );
                      return (
                        <div key={exp} className={`text-xs truncate ${isUnused ? 'text-orange-600' : 'text-gray-700'}`}>
                          {exp}
                          {isUnused && <span className="ml-1 text-orange-500">⚠</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedNode.dependents > 0 && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>Imported by {selectedNode.dependents} files</strong> — changes here impact multiple parts of the codebase
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FrontendTypeScriptGraphPage;
